import JSZip from "jszip";
import type { Order, OrderAddonItem, OrderLensNumber } from "../types/order";
import { normalizeDecimal } from "./decimal";

/** Escape XML special characters */
function escapeXml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

/** Render a single XML tag. Empty/undefined values produce self-closing tags. */
function tag(name: string, value?: string | number | null): string {
  const v = normalizeDecimal(value);
  return v ? `<${name}>${escapeXml(v)}</${name}>` : `<${name} />`;
}

/** Get lens detail value for a given eye side */
function lens(order: Order, eye: "re" | "le", field: string): string {
  return order.lens_detail?.[eye]?.[field] ?? "";
}

/** Prefix positive numeric values with '+' sign */
function signedValue(value: string): string {
  if (!value) return value;
  // Already signed — avoid double prefix (e.g. "+1.25" → "+1.25", not "++1.25")
  if (value.startsWith("+") || value.startsWith("-")) return value;
  const num = parseFloat(value);
  if (!isNaN(num) && num > 0) return `+${value}`;
  return value;
}

/** Resolve patient title with the same lookup order the CRM form uses. */
function resolvePatientTitle(order: Order): string {
  const client = order.client as Record<string, unknown> | undefined;
  const candidates = [client?.title, client?.prefix, client?.salutation];
  for (const c of candidates) {
    if (typeof c === "string" && c.trim()) return c;
  }
  return "คุณ";
}

/** Build the LMTB_PO_HEAD section */
function buildHead(order: Order): string {
  const orderType = order.order_type === "RX" ? "P" : "S";
  return `<LMTB_PO_HEAD>
${tag("POHD_User_ID")}
${tag("POHD_FORWARD", "false")}
${tag("POHD_TRAN_TYPE", "0")}
${tag("POHD_CUST_CD", order.customer?.ecp_code)}
${tag("POHD_APPLICATION_NAME", "WOS")}
${tag("POHD_VERSION", "1.0")}
${tag("POHD_SOURCE_CS", "EDT")}
${tag("POHD_ENTRY_DATE_TIME")}
${tag("POHD_ORDER_TYPE", orderType)}
${tag("POHD_URGENT", "0")}
${tag("POHD_SOURCE_REF", order.reference_no)}
${tag("POHD_HANDLING_REF", order.order_id)}
${tag("POHD_COMMENT_1", order.notes)}
${tag("POHD_COMMENT_2")}
${tag("POHD_COMMENT_3")}
${tag("POHD_PATIENT_TITLE", resolvePatientTitle(order))}
${tag("POHD_PATIENT_FIRST_NAME", order.client?.name)}
${tag("POHD_PATIENT_LAST_NAME", order.client?.surname)}
${tag("POHD_PRT_GURANTEE", "0")}
${tag("POHD_CONTRACT", "0")}
${tag("POHD_VAASSCN", "0")}
${tag("POHD_TRACE", "0")}
</LMTB_PO_HEAD>`;
}

/** Build the LMTB_PO_ADDRESS section */
function buildAddress(order: Order): string {
  const addr = order.ship_to?.address;
  const addr1 = typeof addr === "string" ? addr : "";
  return `<LMTB_PO_ADDRESS>
${tag("POAD_CUST_CD", order.ship_to?.code)}
${tag("POAD_DEL_CUST_CD")}
${tag("POAD_DEL_ADDR1", addr1)}
${tag("POAD_DEL_ADDR2")}
${tag("POAD_DEL_CITY")}
${tag("POAD_DEL_PIN_CODE")}
</LMTB_PO_ADDRESS>`;
}

function mapFrameType(frameType?: string | null): string | undefined {
  if (!frameType) return undefined;
  if (frameType.includes("กรอบเต็ม")) return "P";
  if (frameType.includes("กรอบเจาะ")) return "L";
  if (frameType.includes("กรอบเซาะร่อง")) return "N";
  if (frameType.includes("กรอบเหล็ก")) return "W";
  return undefined;
}

/** Build the LMTB_PO_TRACE section */
function buildTrace(order: Order): string {
  return `<LMTB_PO_TRACE>
${tag("POTR_A", order.frame_detail?.width)}
${tag("POTR_B", order.frame_detail?.height)}
${tag("POTR_DBL", order.frame_detail?.dbl)}
${tag("POTR_FRAME", mapFrameType(order.frame_detail?.type))}
${tag("POTR_SHAPE", order.frame_detail?.lens_shape)}
${tag("POTR_36POINTS")}
</LMTB_PO_TRACE>`;
}

/** Build a single LMTB_PO_LINE for a lens (product) line */
function buildProductLine(
  order: Order,
  side: number,
  eye: "re" | "le",
): string {
  return `<LMTB_PO_LINE>
${tag("POLN_COMM_DIAM")}
${tag("POLN_PROD_TYPE", "S")}
${tag("POLN_PROD_CD", order.product?.code)}
${tag("POLN_SOURCE_PROD_DESC", order.product?.custom_description || order.product_description || order.product?.description)}
${tag("POLN_QUANTITY", "1")}
${tag("POLN_SIDE", String(side))}
${tag("POLN_INFO_1", signedValue(lens(order, eye, "sph") || lens(order, eye, "sphere")))}
${tag("POLN_INFO_2", signedValue(lens(order, eye, "cyl") || lens(order, eye, "cylinder")))}
${tag("POLN_INFO_3", lens(order, eye, "axis"))}
</LMTB_PO_LINE>`;
}

/** Build a single LMTB_PO_LINE for a value-add addon */
function buildAddonLine(
  addon: OrderAddonItem,
  lensNumber?: OrderLensNumber,
): string {
  const cd =
    addon.product_addons?.index_key ??
    (addon as unknown as Record<string, unknown>).index_key;
  const qty = lensNumber === "PAIR" ? "2" : "1";
  return `<LMTB_PO_LINE>
${tag("POLN_COMM_DIAM")}
${tag("POLN_PROD_TYPE", "V")}
${tag("POLN_PROD_CD", cd as string | undefined)}
${tag("POLN_QUANTITY", qty)}
${tag("POLN_SIDE", "0")}
${tag("POLN_INFO_1", "......")}
${tag("POLN_INFO_2", "......")}
${tag("POLN_INFO_3", "......")}
</LMTB_PO_LINE>`;
}

/** Build the ORDERLINES section */
function buildOrderLines(order: Order): string {
  const lines: string[] = [];

  // Product lines per eye side
  if (order.lens_number === "PAIR") {
    lines.push(buildProductLine(order, 1, "re"));
    lines.push(buildProductLine(order, 2, "le"));
  } else {
    // Single lens — determine which eye has data
    const hasRe =
      lens(order, "re", "sph") ||
      lens(order, "re", "sphere") ||
      lens(order, "re", "cyl");
    const hasLe =
      lens(order, "le", "sph") ||
      lens(order, "le", "sphere") ||
      lens(order, "le", "cyl");
    if (hasRe) lines.push(buildProductLine(order, 1, "re"));
    if (hasLe) lines.push(buildProductLine(order, 2, "le"));
    if (!hasRe && !hasLe) {
      lines.push(buildProductLine(order, 1, "re"));
    }
  }

  // Value-add addon lines
  const vaAddons = (order.addons ?? []).filter((a) => {
    const t = a.product_addons?.type ?? (a as unknown as Record<string, unknown>).type;
    return t === "VALUE_ADD";
  });
  for (const addon of vaAddons) {
    lines.push(buildAddonLine(addon, order.lens_number));
  }

  return `<ORDERLINES>
${lines.join("\n")}
</ORDERLINES>`;
}

/** Build a single LMTB_PO_PRESC for one eye */
function buildPrescription(
  order: Order,
  eyeLabel: "R" | "L",
  eye: "re" | "le",
): string {
  const height =
    lens(order, eye, "height") || lens(order, eye, "h") || "";
  const reqDia =
    eye === "re"
      ? order.frame_detail?.req_dia_right
      : order.frame_detail?.req_dia_left;
  const progLen = (order.lens_detail as Record<string, unknown> | undefined)
    ?.pl as string | number | null | undefined;
  return `<LMTB_PO_PRESC>
${tag("POPR_PERSO_LATERALITY")}
${tag("POPR_REVISION", "1")}
${tag("POPR_EYE", eyeLabel)}
${tag("POPR_PROD_CD", order.product?.code)}
${tag("POPR_SPHERE", lens(order, eye, "sph") || lens(order, eye, "sphere"))}
${tag("POPR_CYLINDER", lens(order, eye, "cyl") || lens(order, eye, "cylinder"))}
${tag("POPR_ADDITION", lens(order, eye, "add"))}
${tag("POPR_AXIS", lens(order, eye, "axis"))}
${tag("POPR_REQ_DIAMETER", reqDia)}
${tag("POPR_PD", lens(order, eye, "pd"))}
${tag("POPR_HEIGHT", height)}
${tag("POPR_TRACE", "0")}
${tag("POPR_REQ_ET")}
${tag("POPR_REQ_BASE")}
${tag("POPR_OPTIONAL_1")}
${tag("POPR_OPTIONAL_2")}
${tag("POPR_OPTIONAL_3")}
${tag("POPR_WEARER_INITIALS")}
${tag("POPR_HD_EYE_COEFF")}
${tag("POPR_STAB_COEFF")}
${tag("POPR_PROG_LEN", progLen)}
${tag("POPR_DLE")}
${tag("POPR_INS_NR_VISION")}
${tag("POPR_HEAD_CAPE_ANGLE")}
${tag("POPR_EYE_ROTN_CNTR")}
${tag("POPR_ENGRAVE_CODE")}
${tag("POPR_ELLIPTICAL_HEIGHT")}
</LMTB_PO_PRESC>`;
}

/** Build the PRESCRIPTION section */
function buildPrescriptions(order: Order): string {
  return `<PRESCRIPTION>
${buildPrescription(order, "R", "re")}
${buildPrescription(order, "L", "le")}
</PRESCRIPTION>`;
}

const VA_TYPE_MAP: Record<string, string> = {
  VALUE_ADD: "V",
  INSTRUCTION: "X",
};

/** Build the VA section */
function buildVA(order: Order): string {
  const addons = order.addons ?? [];
  const vaAddons = addons.filter((a) => {
    const t = a.product_addons?.type ?? (a as unknown as Record<string, unknown>).type;
    return t === "VALUE_ADD" || t === "INSTRUCTION";
  });
  if (vaAddons.length === 0) {
    return `<VA>
</VA>`;
  }

  const lineNumbers = order.lens_number === "PAIR" ? [1, 2] : [1];
  const entries = vaAddons.flatMap((addon) => {
    const cd = addon.product_addons?.index_key ?? (addon as unknown as Record<string, unknown>).index_key;
    const qty = addon.quantity ?? addon.product_addons?.quantity ?? 1;
    const addonType = (addon.product_addons?.type ?? (addon as unknown as Record<string, unknown>).type) as string;
    const prodType = VA_TYPE_MAP[addonType] || "V";
    return lineNumbers.map(
      (lineNo) => `<LMTB_PO_VA_ASSOCN>
${tag("POVA_VA_QUANTITY", String(qty))}
${tag("POVA_LINE_NO", String(lineNo))}
${tag("POVA_VA_CD", cd as string)}
${tag("POVA_PROD_TYPE", prodType)}
</LMTB_PO_VA_ASSOCN>`,
    );
  });

  return `<VA>
${entries.join("\n")}
</VA>`;
}

/** Build complete XML for an order */
export function buildOrderXml(order: Order): string {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<LMTB_MAIN>
${buildHead(order)}
${buildAddress(order)}
${buildTrace(order)}
${buildOrderLines(order)}
${buildPrescriptions(order)}
${buildVA(order)}
</LMTB_MAIN>`;
}

/** Trigger browser download of a zip containing the XML and an .ok file */
export async function downloadOrderXml(order: Order): Promise<void> {
  const baseName = order.order_id || order.id || "order";
  const xml = buildOrderXml(order);

  const zip = new JSZip();
  zip.file(`${baseName}.xml`, xml);
  zip.file(`${baseName}.xml.OK`, "");

  const blob = await zip.generateAsync({ type: "blob" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${baseName}.zip`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
